import { translate } from '@teamleader/i18n';
import React, { useCallback, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import * as styles from './styles.css';
import Sign from '../../../Svg/90x90_sign.svg';
import Box from '../Box';
import FormItem from '../FormItem';
import FormLabel from '../FormLabel';
import Link from '../Link';

interface DrawSignatureProps {
  onChange: (signature: string) => void;
  handleReset?: () => void;
  width?: number;
  height?: number;
  showPlaceholder?: boolean;
  canResetMethod?: boolean;
}

const DrawSignature: React.FC<DrawSignatureProps> = ({
  onChange,
  handleReset,
  width,
  height = 120,
  showPlaceholder,
  canResetMethod = true,
}) => {
  const ref = useRef<SignatureCanvas>(null);
  const [computedWidth, setComputedWidth] = useState(263);
  const [isChanged, setIsChanged] = useState(false);

  const handleDrawStart = useCallback(() => {
    if (!isChanged) {
      setIsChanged(true);
    }

    ref.current!.getCanvas().focus();
  }, [isChanged]);

  const handleDrawEnd = useCallback(() => {
    if (!ref.current) {
      return;
    }

    ref.current.getCanvas().blur();

    onChange(ref.current.toDataURL());
  }, [onChange]);

  const computeWidth = useCallback((node?: HTMLElement) => {
    if (!node) {
      return;
    }

    setComputedWidth(node.clientWidth);
  }, []);

  const handleLocalReset = useCallback(() => {
    if (ref.current) {
      ref.current.clear();
      setIsChanged(false);
    }

    handleReset!();
  }, [handleReset]);

  return (
    <Box ref={computeWidth}>
      <FormItem
        label={
          <FormLabel display="flex" justifyContent="space-between">
            {translate('signSidebar.signature')}
            {handleReset && (
              <Link onClick={handleLocalReset} disabled={!canResetMethod && !isChanged}>
                {translate('actions.reset')}
              </Link>
            )}
          </FormLabel>
        }
      >
        <Box className={styles['canvas-wrapper']}>
          <SignatureCanvas
            ref={ref}
            onBegin={handleDrawStart}
            onEnd={handleDrawEnd}
            canvasProps={{
              className: styles['draw-signature'],
              width: width || computedWidth,
              height,
              tabIndex: 0,
            }}
          />
          {!isChanged && showPlaceholder && (
            <Box className={styles['placeholder']}>
              <Sign className={styles['placeholder-icon']} />
              <p>{translate('actions.draw')}</p>
            </Box>
          )}
        </Box>
      </FormItem>
    </Box>
  );
};

export default DrawSignature;
